// Assistir uma sala: a plateia vê a mesa sem a mão de ninguém, com as reações da faixa Plateia, Sentar na mesa quando
// há lugar e Sair. Chega pela lista de salas, pela aba Amigos ("Jogando agora") ou pelo link de uma sala cheia.
import { GuardarConta } from './GuardarConta.tsx';
import { useEffect, useState } from 'react';
import { team } from '@truco6/regras';
import type { Seat } from '@truco6/regras';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { config } from '../comum/config.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { Palco } from '../comum/Palco.tsx';
import { Rosto } from '../comum/Rosto.tsx';
import { ir } from '../comum/rotas.ts';
import { useMesa } from '../comum/useMesa.ts';
import { Mesa, type ModoPlateia } from '../mesa/Mesa.tsx';
import { ConexaoPlateia, type EstadoPlateia } from '../mesa/plateia.ts';
import { apelidoDe, rostoDoLugar, trioDaPlateia } from '../mesa/textos.ts';
import { Marca } from '@truco6/ui/Marca';
import { Carregando, Tela } from './comum.tsx';

const ERROS: Record<string, string> = {
  sala_inexistente: 'Essa sala não existe mais ou o seu amigo saiu dela.',
  sala_encerrada: 'A sala foi encerrada.',
  token_invalido: 'Sua sessão venceu. Entre de novo.',
  outra_conexao: 'Você abriu a plateia em outra aba ou aparelho.',
  plateia_cheia: 'A plateia está cheia.',
  tirado_da_plateia: 'O dono tirou você da plateia desta sala.',
};

export function Assistir({ codigo, amigo, sessao, perfil }: { codigo: string | null; amigo: string | null; sessao: Sessao; perfil: PerfilConta }) {
  const [c, setC] = useState<ConexaoPlateia | null>(null);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    if (!codigo && !amigo) return;
    const alvo = codigo ? { codigo } : { amigo: amigo! };
    const x = new ConexaoPlateia(config.mesaUrl, () => sessao.token(), { apelido: perfil.apelido, avatar: perfil.avatar }, alvo).iniciar();
    setC(x);
    return () => x.encerrar();
  }, [codigo, amigo, sessao.id, tentativa]);
  if (!codigo && !amigo) return <Recusa codigo="sala_inexistente" mensagem={ERROS.sala_inexistente!} />;
  return c ? <Plateia c={c} codigo={codigo} denovo={() => setTentativa((n) => n + 1)} /> : <Carregando texto="Abrindo a plateia..." />;
}

function Recusa({ codigo, mensagem, denovo }: { codigo: string; mensagem: string; denovo?: () => void }) {
  return (
    <Tela>
      <Marca altura={40} />
      {codigo === 'sem_plano' && <p className="cadeado-plateia" aria-hidden="true">🔒</p>}
      <p className="status" role="alert">{mensagem}</p>
      {codigo === 'sem_plano' && <button className="btn1 largo" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>VER PLANOS</button>}
      {(codigo === 'outra_conexao' || codigo === 'rede') && denovo && <button className="btn1 largo" onClick={denovo}>ASSISTIR AQUI</button>}
      <button className="btn2" onClick={() => ir({ tela: 'inicio' })}>Ver salas</button>
      <button className="link" onClick={() => ir({ tela: 'inicio' })}>Voltar ao início</button>
    </Tela>
  );
}

function Plateia({ c, codigo, denovo }: { c: ConexaoPlateia; codigo: string | null; denovo: () => void }) {
  const e = useMesa<EstadoPlateia>(c);
  const p = e.plateia;
  useEffect(() => {
    if (p) medirUmaVez('assistir:' + (p.codigo ?? codigo ?? p.nome), 'espectador_entrou', { tipo: p.publica ? 'publica' : 'privada' });
  }, [!!p]);
  if (e.status === 'encerrada' || (e.erro && !p) || (e.erro && e.erro.codigo === 'tirado_da_plateia')) {
    const cod = e.erro?.codigo ?? 'rede';
    if (cod === 'conta_obrigatoria') return <GuardarConta />;
    return <Recusa codigo={cod} mensagem={ERROS[cod] ?? e.erro?.mensagem ?? 'A conexão com a mesa caiu.'} denovo={denovo} />;
  }
  if (!p || !e.sala) return <Carregando texto={e.status === 'reconectando' ? 'Reconectando à plateia...' : 'Abrindo a plateia...'} />;
  // Sentar na mesa: com lugar na sala de espera (ou vaga aberta na partida) e o acesso à sala (o código).
  const acesso = p.codigo ?? codigo;
  const modo: ModoPlateia = {
    quantos: p.plateia,
    podeSentar: !!acesso && p.vagas > 0,
    aoSentar: () => { c.encerrar(); if (acesso) ir({ tela: 'sala', codigo: acesso, ...(p.fase === 'jogo' ? { vaga: 'espectador' as const } : {}) }); },
    aoSair: () => { c.encerrar(); ir({ tela: 'inicio' }); },
    reagir: (emoji) => c.reagir(emoji),
  };
  if (!e.visao || p.fase === 'espera') return <EsperaNaPlateia e={e} modo={modo} />;
  return <Palco computador>{(paisagem, m) => <Mesa c={c} paisagem={paisagem} modo={m} plateia={modo} />}</Palco>;
}

// Antes da partida (ou entre uma partida e a revanche): os lugares e quem já sentou.
export function EsperaNaPlateia({ e, modo }: { e: EstadoPlateia; modo: ModoPlateia }) {
  const p = e.plateia!, sala = e.sala!;
  const pessoas = sala.lugares.filter((l) => l?.tipo === 'pessoa').length;
  return (
    <Tela>
      <header className="topo-sala">
        <div><span>Assistindo</span><b>{p.nome}</b></div>
        <span className="chipbtn plateia" aria-label={`${p.plateia} ${p.plateia === 1 ? 'pessoa assistindo' : 'pessoas assistindo'}`}><span aria-hidden="true">👀</span> {p.plateia}</span>
      </header>
      <p className="status" role="status">{p.fase === 'encerrada' ? 'A sala foi encerrada.' : `A partida ainda não começou. ${pessoas} de 6 na mesa.`}</p>
      <ul className="lista-plateia lugares-plateia">
        {sala.lugares.map((l, i) => {
          const r0 = rostoDoLugar(l), cor = team(i as Seat) === 0 ? 'var(--nos-400)' : 'var(--eles-400)';
          return (
            <li key={i}>
              {l ? <Rosto {...r0} cor={cor} tamanho={36} /> : <span className="lugar-vazio" style={{ borderColor: cor }} aria-hidden="true">+</span>}
              <span className="nome">{l ? apelidoDe(l) : 'Lugar vazio'}</span>
              <small style={{ color: cor }}>{trioDaPlateia(sala.lugares, team(i as Seat)).toUpperCase()}</small>
            </li>
          );
        })}
      </ul>
      {modo.podeSentar && <button className="btn1 largo" onClick={modo.aoSentar}>SENTAR NA MESA</button>}
      <button className="btn2 largo" onClick={modo.aoSair}>Sair</button>
    </Tela>
  );
}
